/**
 * Builds the Vendor Score Card **summary** export: the list view's own columns, for whatever rows
 * the grid is currently showing, with the five score columns shaded the way they are on screen.
 *
 * ExcelJS is pulled in with a dynamic import so it lands in its own lazy chunk and only downloads
 * when someone actually picks Summary from the Downloads menu.
 */

import type { Row } from 'exceljs';
import { VscListRow } from '../models/vsc-gsc-view.model';
import { ScoreBand, scoreBand } from './vsc-score-band';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * The MDS light-theme tokens the grid shades its score circles with, as the ARGB that Excel wants.
 *
 * Written out rather than resolved from `@maersk-global/mds-design-tokens`, because a default JSON
 * import cannot be tree-shaken and would put all 491 tokens in the bundle to read four of them.
 * The light palette is deliberate: a workbook opens on a white sheet whatever theme the app is in.
 *
 * `vsc-summary-export.spec.ts` asserts these against the token package, so they cannot drift.
 */
const FILL_ARGB: Record<Exclude<ScoreBand, ''>, string> = {
  poor: 'FFFCDDE0', // brand_appearance_error_weak_background-color    — rgb(252,221,224)
  average: 'FFFFF1C2', // brand_appearance_warning_weak_background-color  — rgb(255,241,194)
  good: 'FFE2F3FB', // brand_appearance_info_weak_background-color     — rgb(226,243,251)
  excellent: 'FFDCF6D9', // brand_appearance_success_weak_background-color  — rgb(220,246,217)
};

const HEADER_FILL_ARGB = 'FF141414'; // brand_appearance_neutral_default_text-color       — rgb(20,20,20)
const HEADER_TEXT_ARGB = 'FFFFFFFF'; // brand_appearance_neutral_default_background-color — rgb(255,255,255)

/** Same columns, same order, as the list view grid. Keys match `VscListRow`, so a row maps straight. */
export const VSC_SUMMARY_COLUMNS = [
  { header: 'Vendor Code', key: 'vendorCode', width: 14 },
  { header: 'Vendor Name', key: 'vendorName', width: 38 },
  { header: 'Country', key: 'country', width: 22 },
  { header: 'Work Orders', key: 'workOrderCount', width: 14 },
  { header: 'Overall Score', key: 'overallScore', width: 15 },
  { header: 'Lead Time', key: 'leadTimeScore', width: 12 },
  { header: 'Process Compliance', key: 'processComplianceScore', width: 20 },
  { header: 'eEIR', key: 'eEIRScore', width: 10 },
  { header: 'Audit Result', key: 'auditResultScore', width: 14 },
] as const;

/** The columns that carry a 0–5 score, and so get a band colour. */
const SCORE_KEYS = [
  'overallScore',
  'leadTimeScore',
  'processComplianceScore',
  'eEIRScore',
  'auditResultScore',
] as const satisfies readonly (keyof VscListRow)[];

type ExcelJsModule = typeof import('exceljs');

/**
 * ExcelJS ships two builds and the environment decides which one you get. The browser resolves its
 * `browser` field to a UMD bundle, which esbuild re-exports as `default` **only**; Node — and so
 * vitest — resolves the CJS build, where the named exports survive. Destructuring the namespace
 * directly therefore works in the tests and hands back `undefined` in the browser, which surfaces
 * as `Workbook is not a constructor`. Reading through `default` first covers both shapes.
 */
export function resolveExcelJs(imported: ExcelJsModule & { default?: ExcelJsModule }): ExcelJsModule {
  return imported.default ?? imported;
}

export async function buildVscSummaryWorkbook(rows: VscListRow[], month: string): Promise<Blob> {
  const { Workbook } = resolveExcelJs(await import('exceljs'));

  const workbook = new Workbook();
  const sheet = workbook.addWorksheet(sheetName(`Summary ${month}`), {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  sheet.columns = VSC_SUMMARY_COLUMNS.map((column) => ({ ...column }));

  styleHeader(sheet.getRow(1));
  rows.forEach((row) => styleBody(sheet.addRow(row), row));

  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: rows.length + 1, column: VSC_SUMMARY_COLUMNS.length },
  };

  // ExcelJS declares its Buffer as `extends ArrayBuffer`, so it is already a valid BlobPart.
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}

/** e.g. `vendor-score-card-summary-Sept-2026.xlsx` */
export function vscSummaryFileName(month: string): string {
  const period = month.trim().replace(/\s+/g, '-');
  return period ? `vendor-score-card-summary-${period}.xlsx` : 'vendor-score-card-summary.xlsx';
}

/** Excel rejects `: \ / ? * [ ]` in a tab name and truncates past 31 characters. */
export function sheetName(name: string): string {
  const cleaned = name.replace(/[:\\/?*[\]]/g, ' ').trim();
  return cleaned.length > 0 ? cleaned.slice(0, 31) : 'Sheet1';
}

function styleHeader(header: Row): void {
  header.height = 22;
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_TEXT_ARGB } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL_ARGB } };
    cell.alignment = { horizontal: 'left', vertical: 'middle' };
  });
}

function styleBody(added: Row, row: VscListRow): void {
  // The grid abbreviates large counts (1550 -> 1.6K); a spreadsheet wants the real number so it can
  // be summed and sorted, so only the alignment carries over.
  added.getCell('workOrderCount').alignment = { horizontal: 'right' };

  for (const key of SCORE_KEYS) {
    const cell = added.getCell(key);
    cell.alignment = { horizontal: 'center' };

    // A missing score stays an empty cell, which Excel sorts and filters as blank rather than text.
    const band = scoreBand(row[key]);
    if (band === '') continue;

    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL_ARGB[band] } };
  }
}
